



import styled, { css } from 'styled-components';

import { colors, fonts, cardShadow } from '../shared/tokens';
import { GridIcon, ShieldIcon } from '../shared/Icons';



export const Logo = ({ big = false, as = 'div', href }) => {
  const box = big ? 37.992 : 25.992;
  const icon = big ? 20 : 14;
  const radius = big ? 7 : 5;
  const wordSize = big ? 26 : 17;
  const osSize = big ? 13 : 10;
  const tracking = big ? 2.6 : 1.7;

  return (
    <LogoRoot as={as} href={href} $big={big}>
      <LogoBox $size={box} $radius={radius}>
        <GridIcon size={icon} />
      </LogoBox>
      <LogoWord $size={wordSize} $tracking={tracking}>
        UMBRELLA
        <LogoOs $size={osSize}>OS</LogoOs>
      </LogoWord>
    </LogoRoot>
  );
};

const LogoRoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $big }) => ($big ? 12 : 10)}px;
  text-decoration: none;
  ${({ href }) => href && css`cursor: pointer;`}
`;

const LogoBox = styled.div`
  flex-shrink: 0;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: 1.5px solid ${colors.cyan};
  border-radius: ${({ $radius }) => $radius}px;
  color: ${colors.cyan};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoWord = styled.p`
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: ${({ $size }) => $size}px;
  line-height: 1;
  letter-spacing: ${({ $tracking }) => $tracking}px;
  text-transform: uppercase;
  color: ${colors.ink};
  white-space: nowrap;
`;

const LogoOs = styled.span`
  font-size: ${({ $size }) => $size}px;
  color: ${colors.cyan};
`;





export const AuthCard = styled.div`
  position: relative;
  width: 400px;
  max-width: 100%;
  padding: 36.75px 40.75px 32.75px;
  border: 0.75px solid ${colors.cyanBorder};
  border-radius: 14px;
  background: ${colors.cardBg};
  box-shadow: ${cardShadow};
  display: flex;
  flex-direction: column;
  align-items: ${({ $center }) => ($center ? 'center' : 'flex-start')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 28px;
    width: 342.5px;
    max-width: calc(100% - 56px);
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0%,
      rgba(56, 200, 212, 0.5) 50%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: inset 0px 1px 0px 0px rgba(255, 255, 255, 0.05);
  }
`;

export const CardTitle = styled.h1`
  margin: 27.5px 0 0.5px;
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 30px;
  line-height: 45px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.ink};
`;

export const CardSubtitle = styled.p`
  margin-top: 4px;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 13px;
  line-height: 19.5px;
  color: ${colors.muted};
`;



export const FieldGroup = styled.div`
  width: 100%;
  margin-top: ${({ $gap = 28 }) => $gap}px;
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 7px;
  font-family: ${fonts.mono};
  font-weight: 500;
  font-size: 10px;
  line-height: 15px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${colors.muted};
`;

export const InputShell = styled.div`
  position: relative;
  width: 100%;
`;

export const TextInput = styled.input`
  width: 100%;
  height: 46.488px;
  padding: 12.75px 16.75px;
  ${({ $hasTrailing }) => $hasTrailing && css`padding-right: 42px;`}
  border: 0.75px solid ${colors.inputBorder};
  border-radius: 6px;
  background: ${colors.inputBg};
  color: ${colors.ink};
  font-family: ${fonts.body};
  font-weight: 400;
  font-size: 14px;
  text-align: left;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${colors.placeholder};
  }

  &:focus {
    border-color: ${colors.cyan};
    box-shadow: 0 0 0 3px rgba(56, 200, 212, 0.12);
  }
`;

export const TrailingButton = styled.button`
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.muted2};
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.cyan};
  }
`;



export const PrimaryButton = styled.button`
  width: 100%;
  height: 50.484px;
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border: none;
  border-radius: 6px;
  background: ${colors.cyan};
  color: ${colors.bg};
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover {
    background: #4ad6e2;
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  span {
    font-family: ${fonts.condensed};
    font-weight: 800;
    font-size: 15px;
    line-height: 22.5px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
`;



export const AuthorizedNotice = () => (
  <NoticeRoot>
    <NoticeIcon>
      <ShieldIcon size={14} />
    </NoticeIcon>
    <NoticeText>
      <strong>Authorized access only.</strong> This platform is restricted to
      licensed operators and administrators. Unauthorized access attempts are
      logged and monitored.
    </NoticeText>
  </NoticeRoot>
);

const NoticeRoot = styled.div`
  width: 100%;
  margin-top: 18px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10.75px 12.75px;
  border: 0.75px solid ${colors.cyanBorderSoft};
  border-radius: 6px;
  background: ${colors.cyanFill};
`;

const NoticeIcon = styled.div`
  flex-shrink: 0;
  padding-top: 1px;
  color: ${colors.cyan};
`;

const NoticeText = styled.p`
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 11px;
  line-height: 17.05px;
  color: ${colors.muted};

  strong {
    font-weight: 500;
    color: ${colors.textSoft};
  }
`;



export const StatusFooter = () => (
  <StatusRoot>
    <StatusLeft>
      <StatusDot />
      <StatusText>All systems operational</StatusText>
    </StatusLeft>
    <StatusText>v3.2.1 · UOS</StatusText>
  </StatusRoot>
);

const StatusRoot = styled.div`
  width: 100%;
  margin-top: 20px;
  padding-top: 16.75px;
  border-top: 0.75px solid ${colors.hairline};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatusLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  background: ${colors.green};
`;

const StatusText = styled.p`
  font-family: ${fonts.mono};
  font-weight: 400;
  font-size: 9px;
  line-height: 13.5px;
  letter-spacing: 0.72px;
  color: ${colors.muted2};
  white-space: nowrap;
`;
